import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  get,
  isEmptyCollection,
  SiteConfigManager,
  SportManager,
  useSportsbookState,
} from '@atlas/core';
import { COMPONENT_TYPES, sportsbookRoutes } from '@atlas/routing';
import { Button, ChipsItem, Label, Loader } from '@atlas/ui';

import MarketTypeSelector from 'common/MarketTypeSelector/MarketTypeSelector';
import initialTournamentShape from 'common/PropTypes/initialTournamentShape';
import { defaultRoutes } from 'components/MainRouter/constants';
import PrematchSportsbookLoader from 'components/Pages/PrematchSportsbookLoader';
import SelectionTournaments from 'components/SportsbookSelections/SelectionTournaments';
import useSelections from 'components/SportsbookSelections/useSelections';

const SportsbookSelectionsLoader = ({ children, isEmptyTournaments, loading }) => {
  if (loading) {
    return (
      <div className="empty-state-wrapper with-header">
        <Loader className="absolute" />
      </div>
    );
  }

  if (isEmptyTournaments) {
    const { sportLive, sportPrematch } = defaultRoutes;

    return (
      <div className="empty-state-wrapper with-header">
        <div className="empty-state-text">
          <Label className="title-text" message="this_selection_is_empty_message" />
          <Label className="sub-title-text" message="this_selection_is_empty_description" />
          <div className="sticky-bottom-buttons-container column button-no-markets">
            <Link aria-label="Prematch" to={sportPrematch}>
              <Button className="btn big secondary " label="go_to_sport" />
            </Link>

            <Link aria-label="Live" to={sportLive}>
              <Button className="btn big primary" label="go_to_live" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

SportsbookSelectionsLoader.propTypes = {
  loading: PropTypes.bool,
  tournaments: PropTypes.arrayOf(initialTournamentShape).isRequired,
};

const SportsbookSelections = () => {
  const { selectionId: selectedSelectionId } = useParams();
  const { filter, sports } = useSportsbookState();
  const { timeHours: timeHoursFilter } = filter;

  const { isLoading, isSelectionsLoading, selections, tournaments } = useSelections({
    selectedSelectionId,
  });

  const { push: historyPush } = useHistory();
  const [loading, setLoading] = useState(isLoading);
  const [isEmptyTournaments, setIsEmptyTournaments] = useState(true);
  const [tournamentsData, setTournamentsData] = useState([]);
  const sportPrematchPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_PREMATCH);
  const mainMarketsChoice = !!SiteConfigManager.getConfig(
    'config.config.sportsbook.mainMarketsChoice',
  );

  useEffect(() => {
    if ((!isSelectionsLoading && isEmptyCollection(selections)) || timeHoursFilter) {
      historyPush(`${sportPrematchPath}/${get(sports, '0.urlName')}`);
    }
  }, [historyPush, isSelectionsLoading, selections, sportPrematchPath, sports, timeHoursFilter]);

  useEffect(() => {
    if (!selectedSelectionId) return;
    setLoading(true);
    SportManager.findTournaments({ filter: { tournamentIds: tournaments.map(value => value.id) } })
      .then(({ data: tournamentsData }) => {
        const preparedTournaments = tournaments.reduce((acc, tournament, index) => {
          acc[index] = tournament;
          const tournamentData = tournamentsData.find(value => value.id === tournament.id);
          acc[index] = {
            ...acc[index],
            imagePaths: tournamentData?.imagePaths,
            sport: sports.find(sport => sport.id === tournament.sportId),
            sportFormMainTypes: tournamentData?.sportForm && tournamentData.sportForm,
          };
          return acc;
        }, []);

        setIsEmptyTournaments(isEmptyCollection(preparedTournaments));
        setTournamentsData(preparedTournaments);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tournaments]);

  useEffect(() => {
    if (selections.length && !selectedSelectionId) {
      historyPush(`${sportPrematchPath}/${sportsbookRoutes.selections}/${selections[0]?.id}`);
    }
  }, [historyPush, selectedSelectionId, selections, sportPrematchPath]);

  return (
    <PrematchSportsbookLoader>
      <div className="history-filter">
        <div className="chips-list">
          {selections
            .sort((a, b) => a.orderPosition - b.orderPosition)
            .map(({ id, name }) => {
              return (
                <Link key={id} to={`${id}`}>
                  <ChipsItem active={Number(selectedSelectionId) === id} key={id} label={name} />
                </Link>
              );
            })}
        </div>
      </div>

      <SportsbookSelectionsLoader isEmptyTournaments={isEmptyTournaments} loading={loading}>
        {mainMarketsChoice && <MarketTypeSelector />}

        <div className="selections-tournaments">
          <SelectionTournaments tournaments={tournamentsData} />
        </div>
      </SportsbookSelectionsLoader>
    </PrematchSportsbookLoader>
  );
};

export default SportsbookSelections;
